import { Component, h, Host, Prop, State } from "@stencil/core";
import {
    ContentDataProvider,
    ContentDataProviderState,
    getContentDataProvider,
} from "../../../apis/stateful-content";
import {
    getTranslateFn,
    TranslateFn,
    Translations,
} from "../stateful-content-utils";
import { DownloadItem, DownloadGroup } from "./stateful-downloads-interface";

const baseClass = "ux-iws-stateful-downloads";

@Component({
    tag: "ux-iws-stateful-downloads",
    styleUrl: "stateful-downloads.scss",
    shadow: false,
})
export class UxIwsStatefulDownloads {
    private dataProvider: ContentDataProvider<DownloadItem>;
    private translate: TranslateFn;

    /** scope used to retrieve the content data provider */
    @Prop() scope: string = "";
    /** endpoint for content data */
    @Prop() url: string = "";
    /** JSON string or object with translations */
    @Prop() translations: Translations | string = "";

    @State() groups: DownloadGroup[] = [];

    componentWillLoad() {
        this.translate = getTranslateFn(this.translations);
        this.dataProvider = getContentDataProvider<DownloadItem>(this.scope);
        this.dataProvider.subscribe(this.handleDataProviderState);
        if (this.url !== "") {
            this.dataProvider.setEndpoint(this.url);
        }
        this.dataProvider.getData();
    }

    private handleDataProviderState = ({ data }: ContentDataProviderState) => {
        if (data === null) {
            return;
        }
        const groups: DownloadGroup[] = [];
        data.items.forEach((item) => {
            const group = groups.find(
                ({ category }) => category === item.category
            );
            if (group === undefined) {
                const groupTitle = this.translate(
                    `category.values.${item.category}`
                );
                groups.push({
                    label: groupTitle,
                    category: item.category,
                    items: [item],
                });
            } else {
                group.items.push(item);
            }
        });
        this.groups = groups.sort(this.sortGroups);
    };

    private sortGroups = (a: DownloadGroup, b: DownloadGroup): number => {
        const sortArray: Array<number | string> | null =
            this.dataProvider.data?.schema?.category?.order || null;
        if (Array.isArray(sortArray)) {
            return (
                sortArray.indexOf(a.category) - sortArray.indexOf(b.category)
            );
        }
        return 0;
    };

    private renderField = (item: DownloadItem, key: string) => {
        const { translate } = this;
        const value: string = Array.isArray(item[key])
            ? (item[key] as string[])
                  .map((value) => {
                      return translate(`${key}.values.${value}`) || value;
                  })
                  .join(", ")
            : translate(`${item[key]}`) || item[key];
        const fieldLabel = this.translate(`${key}.label`);
        return (
            <div class={`${baseClass}__field`}>
                <dt class={`${baseClass}__field-label ux-label`}>
                    {fieldLabel || key}
                </dt>
                <dd class={`${baseClass}__field-value ux-text-normal`}>
                    {value}
                </dd>
            </div>
        );
    };

    private getDownloadAttributes(item: DownloadItem) {
        const isPathValid = item.path && item.path.length > 0;
        return {
            isPathValid,
            href: isPathValid ? item.path : undefined,
            download: isPathValid ? item.path.split("/").pop() : undefined,
            className: `ux-button ux-button--white ${
                isPathValid ? "" : "ux-button--disabled"
            }`,
        };
    }

    render() {
        return (
            <Host>
                {this.groups.map((group) => (
                    <ux-accordion key={group.category}>
                        <div
                            slot="title"
                            class="ux-text-title ux-text-title--light"
                        >
                            {group.label || group.category}
                        </div>
                        <div>
                            {group.items.map((item) => {
                                const {
                                    isPathValid,
                                    href,
                                    download,
                                    className,
                                } = this.getDownloadAttributes(item);

                                return (
                                    <div class="ux-u-shadow ux-u-mb-xs ux-u-px-xxs ux-u-px-md-xs ux-u-pt-xs ux-u-pb-zero">
                                        <h4 class="ux-text-headline--small ux-u-mt-zero ux-u-mb-xs">
                                            {item.name}
                                        </h4>
                                        <div class={`${baseClass}__details`}>
                                            {["date", "languages"].map((key) =>
                                                this.renderField(item, key)
                                            )}
                                            <div class={`${baseClass}__cta`}>
                                                <a
                                                    {...(href && {
                                                        href,
                                                    })}
                                                    {...(download && {
                                                        download,
                                                    })}
                                                    class={className}
                                                    aria-disabled={!isPathValid}
                                                >
                                                    {this.translate(
                                                        `cta.label`
                                                    )}
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </ux-accordion>
                ))}
            </Host>
        );
    }
}
